import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

import { 
  ArrowLeft, Users, DollarSign, FileText, Plus, Search, 
  Download, Calendar, Clock, CheckCircle, AlertCircle,
  User, Building, CreditCard, Printer, Trash2, Edit, X
} from 'lucide-react';

const PayrollPage = () => {
  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showProcessPayroll, setShowProcessPayroll] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [message, setMessage] = useState(null);
  const [showEditRate, setShowEditRate] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [newPayRate, setNewPayRate] = useState('');
  const [showEditPayroll, setShowEditPayroll] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [editPayrollForm, setEditPayrollForm] = useState({
    hours_worked: '',
    overtime_hours: '',
    pay_date: '',
    pay_period_start: '',
    pay_period_end: '',
    other_deductions: ''
  });

  // Form states
  const [employeeForm, setEmployeeForm] = useState({
    first_name: '',
    last_name: '',
    ssn: '',
    date_of_birth: '',
    hire_date: '',
    department: 'OJT Training',
    position: '',
    pay_type: 'hourly',
    pay_rate: '',
    address: '',
    city: 'Memphis',
    state: 'TN',
    zip_code: '',
    phone: '',
    email: '',
    filing_status: 'single',
    allowances: 0
  });

  const [payrollForm, setPayrollForm] = useState({
    employee_id: '',
    pay_period_start: '',
    pay_period_end: '',
    pay_date: '',
    hours_worked: 0,
    overtime_hours: 0,
    other_deductions: 0
  });

  const API_URL = API_HOST;

  useEffect(() => {
    fetchEmployees();
    fetchPayrollHistory();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payroll/employees`);
      const data = await response.json();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrollHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payroll/all`);
      const data = await response.json();
      setPayrollHistory(data);
    } catch (error) {
      console.error('Error fetching payroll history:', error);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/payroll/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...employeeForm,
          pay_rate: parseFloat(employeeForm.pay_rate),
          allowances: parseInt(employeeForm.allowances)
        })
      });
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'Employee added successfully!' });
        setShowAddEmployee(false);
        fetchEmployees();
        setEmployeeForm({
          first_name: '', last_name: '', ssn: '', date_of_birth: '', hire_date: '',
          department: 'OJT Training', position: '', pay_type: 'hourly', pay_rate: '',
          address: '', city: 'Memphis', state: 'TN', zip_code: '', phone: '', email: '',
          filing_status: 'single', allowances: 0
        });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to add employee' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error adding employee' });
    }
  };

  const handleProcessPayroll = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/payroll/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payrollForm,
          hours_worked: parseFloat(payrollForm.hours_worked),
          overtime_hours: parseFloat(payrollForm.overtime_hours),
          other_deductions: parseFloat(payrollForm.other_deductions)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `Payroll processed! Check #${data.payroll.check_number}` });
        setShowProcessPayroll(false);
        fetchPayrollHistory();
        fetchEmployees();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to process payroll' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error processing payroll' });
    }
  };

  const openProcessPayroll = (employee) => {
    setSelectedEmployee(employee);
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    
    setPayrollForm({
      employee_id: employee.id,
      pay_period_start: twoWeeksAgo.toISOString().split('T')[0],
      pay_period_end: today.toISOString().split('T')[0],
      pay_date: today.toISOString().split('T')[0],
      hours_worked: 80,
      overtime_hours: 0,
      other_deductions: 0
    });
    setShowProcessPayroll(true);
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete ${employeeName}? This will also delete all their payroll history.`)) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/payroll/employees/${employeeId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Employee deleted successfully!' });
        fetchEmployees();
        fetchPayrollHistory();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete employee' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting employee' });
    }
  };

  const handleDeletePayrollEntry = async (payrollId, checkNumber) => {
    if (!window.confirm(`Are you sure you want to delete check #${checkNumber}? This will reverse the YTD totals.`)) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/payroll/entry/${payrollId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Payroll entry deleted and YTD reversed!' });
        fetchEmployees();
        fetchPayrollHistory();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to delete payroll entry' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting payroll entry' });
    }
  };

  const openEditRate = (employee) => {
    setEditingEmployee(employee);
    setNewPayRate(employee.pay_rate.toString());
    setShowEditRate(true);
  };

  const handleUpdatePayRate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/payroll/employees/${editingEmployee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pay_rate: parseFloat(newPayRate) })
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Pay rate updated to $${parseFloat(newPayRate).toFixed(2)}!` });
        setShowEditRate(false);
        setEditingEmployee(null);
        fetchEmployees();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update pay rate' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating pay rate' });
    }
  };

  const openEditPayroll = (entry) => {
    setEditingPayroll(entry);
    setEditPayrollForm({
      hours_worked: entry.hours_worked?.toString() || '0',
      overtime_hours: entry.overtime_hours?.toString() || '0',
      pay_date: entry.pay_date || '',
      pay_period_start: entry.pay_period_start || '',
      pay_period_end: entry.pay_period_end || '',
      other_deductions: entry.other_deductions?.toString() || '0'
    });
    setShowEditPayroll(true);
  };

  const handleUpdatePayroll = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/payroll/entry/${editingPayroll.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hours_worked: parseFloat(editPayrollForm.hours_worked),
          overtime_hours: parseFloat(editPayrollForm.overtime_hours),
          pay_date: editPayrollForm.pay_date,
          pay_period_start: editPayrollForm.pay_period_start,
          pay_period_end: editPayrollForm.pay_period_end,
          other_deductions: parseFloat(editPayrollForm.other_deductions)
        })
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `Check #${editingPayroll.check_number} updated! Taxes recalculated.` });
        setShowEditPayroll(false);
        setEditingPayroll(null);
        fetchEmployees();
        fetchPayrollHistory();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to update payroll entry' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error updating payroll entry' });
    }
  };

  const formatSSN = (ssn) => {
    if (!ssn) return '';
    return `XXX-XX-${ssn.slice(-4)}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1e3a5f 0%, #334155 100%)', 
        padding: '20px 40px',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={20} />
            Back to Dashboard
          </Link>
          <h1 style={{ margin: 0 }}>MedCare Payroll System</h1>
          <div style={{ width: '150px' }}></div>
        </div>
        
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {[
            { id: 'employees', label: 'Employees', icon: Users },
            { id: 'payroll', label: 'Process Payroll', icon: DollarSign },
            { id: 'history', label: 'Payroll History', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 20px',
                background: activeTab === tab.id ? 'white' : 'rgba(255,255,255,0.1)',
                color: activeTab === tab.id ? '#1e3a5f' : 'white',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal'
              }}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '12px 40px',
          background: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: message.type === 'success' ? '#166534' : '#dc2626',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
          <button 
            onClick={() => setMessage(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
          >×</button>
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '24px 40px' }}>
        
        {/* Quick Actions Bar */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '16px 20px', 
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileText size={20} color="#1e3a5f" />
            <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Need blank check forms?</span>
          </div>
          <a
            href={`${API_URL}/api/payroll/blank-check`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-blank-check"
            style={{
              background: '#f1f5f9',
              color: '#1e3a5f',
              padding: '8px 16px',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              border: '1px solid #e2e8f0'
            }}
          >
            <Download size={16} />
            Download Blank Check & Stub
          </a>
        </div>
        
        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, color: '#1e3a5f' }}>Employee Directory</h2>
              <button
                onClick={() => setShowAddEmployee(true)}
                style={{
                  background: '#22c55e',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 'bold'
                }}
              >
                <Plus size={18} />
                Add Employee
              </button>
            </div>

            {/* Employee Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
              {employees.map(emp => (
                <div key={emp.id} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', color: '#1e3a5f' }}>
                        {emp.first_name} {emp.last_name}
                      </h3>
                      <span style={{ 
                        background: '#dbeafe', 
                        color: '#1d4ed8', 
                        padding: '2px 8px', 
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {emp.employee_id}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        background: emp.status === 'active' ? '#dcfce7' : '#fee2e2',
                        color: emp.status === 'active' ? '#166534' : '#dc2626',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {emp.status?.toUpperCase()}
                      </span>
                      <button
                        onClick={() => handleDeleteEmployee(emp.id, `${emp.first_name} ${emp.last_name}`)}
                        title="Delete Employee"
                        data-testid={`delete-employee-${emp.employee_id}`}
                        style={{
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '12px' }}>
                    <div><strong>Position:</strong> {emp.position}</div>
                    <div><strong>Department:</strong> {emp.department}</div>
                    <div><strong>SSN/State ID:</strong> {formatSSN(emp.ssn)}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span><strong>Pay Rate:</strong> {formatCurrency(emp.pay_rate)}{emp.pay_type === 'hourly' ? '/hr' : '/yr'}</span>
                      <button
                        onClick={() => openEditRate(emp)}
                        title="Edit Pay Rate"
                        data-testid={`edit-rate-${emp.employee_id}`}
                        style={{
                          background: '#dbeafe',
                          color: '#1d4ed8',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '2px 6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '0.7rem'
                        }}
                      >
                        <Edit size={12} />
                        Edit
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ 
                    background: '#f8fafc', 
                    borderRadius: '8px', 
                    padding: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>YTD Earnings</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Gross</div>
                        <div style={{ fontWeight: 'bold', color: '#1e3a5f' }}>{formatCurrency(emp.ytd_gross)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Taxes</div>
                        <div style={{ fontWeight: 'bold', color: '#dc2626' }}>
                          {formatCurrency((emp.ytd_federal_tax || 0) + (emp.ytd_social_security || 0) + (emp.ytd_medicare || 0))}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Net</div>
                        <div style={{ fontWeight: 'bold', color: '#22c55e' }}>{formatCurrency(emp.ytd_net)}</div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => openProcessPayroll(emp)}
                    style={{
                      width: '100%',
                      background: '#1e3a5f',
                      color: 'white',
                      padding: '10px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <DollarSign size={16} />
                    Process Payroll
                  </button>
                </div>
              ))}
              
              {employees.length === 0 && !loading && (
                <div style={{ 
                  gridColumn: '1 / -1',
                  textAlign: 'center', 
                  padding: '60px',
                  background: 'white',
                  borderRadius: '12px'
                }}>
                  <Users size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
                  <h3 style={{ color: '#64748b', margin: '0 0 8px 0' }}>No Employees Yet</h3>
                  <p style={{ color: '#94a3b8', margin: 0 }}>Add your first employee to get started</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payroll History Tab */}
        {activeTab === 'history' && (
          <div>
            <h2 style={{ margin: '0 0 24px 0', color: '#1e3a5f' }}>Payroll History</h2>
            
            <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1e3a5f', color: 'white' }}>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '0.8rem' }}>Check #</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '0.8rem' }}>Employee</th>
                    <th style={{ padding: '12px 10px', textAlign: 'left', fontSize: '0.8rem' }}>Pay Date</th>
                    <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.8rem' }}>Gross</th>
                    <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.8rem' }}>Fed Tax</th>
                    <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.8rem' }}>SS 6.2%</th>
                    <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.8rem' }}>Medicare</th>
                    <th style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.8rem' }}>Net Pay</th>
                    <th style={{ padding: '12px 10px', textAlign: 'center', fontSize: '0.8rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payrollHistory.map((entry, index) => (
                    <tr key={entry.id} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#f8fafc' : 'white' }}>
                      <td style={{ padding: '12px 10px', fontWeight: 'bold', color: '#1e3a5f', fontSize: '0.85rem' }}>{entry.check_number}</td>
                      <td style={{ padding: '12px 10px' }}>
                        <div style={{ fontSize: '0.85rem' }}>{entry.employee_name}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{entry.employee_number}</div>
                      </td>
                      <td style={{ padding: '12px 10px', fontSize: '0.85rem' }}>{entry.pay_date}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.85rem' }}>{formatCurrency(entry.gross_pay)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.85rem', color: '#dc2626' }}>{formatCurrency(entry.federal_tax)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.85rem', color: '#dc2626' }}>{formatCurrency(entry.social_security)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '0.85rem', color: '#dc2626' }}>{formatCurrency(entry.medicare)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 'bold', color: '#22c55e', fontSize: '0.85rem' }}>{formatCurrency(entry.net_pay)}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <a
                            href={`${API_URL}/api/payroll/check/${entry.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`print-check-${entry.check_number}`}
                            style={{
                              background: '#1e3a5f',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Printer size={14} />
                            Print
                          </a>
                          <a
                            href={`${API_URL}/api/payroll/cash-receipt/${entry.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`cash-receipt-${entry.check_number}`}
                            style={{
                              background: '#059669',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Download size={14} />
                            Cash Receipt
                          </a>
                          <button
                            onClick={() => openEditPayroll(entry)}
                            title="Edit Payroll Entry"
                            data-testid={`edit-payroll-${entry.check_number}`}
                            style={{
                              background: '#dbeafe',
                              color: '#1d4ed8',
                              padding: '6px 10px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeletePayrollEntry(entry.id, entry.check_number)}
                            title="Delete Payroll Entry"
                            data-testid={`delete-payroll-${entry.check_number}`}
                            style={{
                              background: '#fee2e2',
                              color: '#dc2626',
                              padding: '6px 10px',
                              borderRadius: '4px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {payrollHistory.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px' }}>
                  <FileText size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
                  <h3 style={{ color: '#64748b', margin: '0 0 8px 0' }}>No Payroll Records</h3>
                  <p style={{ color: '#94a3b8', margin: 0 }}>Process payroll for employees to see history here</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Process Payroll Tab */}
        {activeTab === 'payroll' && (
          <div>
            <h2 style={{ margin: '0 0 24px 0', color: '#1e3a5f' }}>Process Payroll</h2>
            <p style={{ color: '#64748b', marginBottom: '24px' }}>
              Select an employee from the Employees tab to process their payroll, or use the quick process below.
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {employees.filter(e => e.status === 'active').map(emp => (
                <div key={emp.id} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#1e3a5f' }}>{emp.first_name} {emp.last_name}</div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{emp.position}</div>
                  </div>
                  <button
                    onClick={() => openProcessPayroll(emp)}
                    style={{
                      background: '#22c55e',
                      color: 'white',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Run Payroll
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#1e3a5f' }}>Add New Employee</h2>
            
            <form onSubmit={handleAddEmployee}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>First Name *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.first_name}
                    onChange={e => setEmployeeForm({...employeeForm, first_name: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Last Name *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.last_name}
                    onChange={e => setEmployeeForm({...employeeForm, last_name: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>SSN or State ID Number *</label>
                  <input
                    type="text"
                    required
                    placeholder="SSN (123-45-6789) or State ID"
                    value={employeeForm.ssn}
                    onChange={e => setEmployeeForm({...employeeForm, ssn: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Date of Birth *</label>
                  <input
                    type="date"
                    required
                    value={employeeForm.date_of_birth}
                    onChange={e => setEmployeeForm({...employeeForm, date_of_birth: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Hire Date *</label>
                  <input
                    type="date"
                    required
                    value={employeeForm.hire_date}
                    onChange={e => setEmployeeForm({...employeeForm, hire_date: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Position *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.position}
                    onChange={e => setEmployeeForm({...employeeForm, position: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Department</label>
                  <select
                    value={employeeForm.department}
                    onChange={e => setEmployeeForm({...employeeForm, department: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  >
                    <option value="OJT Training">OJT Training</option>
                    <option value="Administration">Administration</option>
                    <option value="Instruction">Instruction</option>
                    <option value="Clinical">Clinical</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Pay Type *</label>
                  <select
                    value={employeeForm.pay_type}
                    onChange={e => setEmployeeForm({...employeeForm, pay_type: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Pay Rate * ({employeeForm.pay_type === 'hourly' ? '$/hour' : '$/year'})
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={employeeForm.pay_rate}
                    onChange={e => setEmployeeForm({...employeeForm, pay_rate: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Filing Status</label>
                  <select
                    value={employeeForm.filing_status}
                    onChange={e => setEmployeeForm({...employeeForm, filing_status: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  >
                    <option value="single">Single</option>
                    <option value="married">Married</option>
                    <option value="head_of_household">Head of Household</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Address *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.address}
                    onChange={e => setEmployeeForm({...employeeForm, address: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>City *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.city}
                    onChange={e => setEmployeeForm({...employeeForm, city: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>ZIP Code *</label>
                  <input
                    type="text"
                    required
                    value={employeeForm.zip_code}
                    onChange={e => setEmployeeForm({...employeeForm, zip_code: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Phone *</label>
                  <input
                    type="tel"
                    required
                    value={employeeForm.phone}
                    onChange={e => setEmployeeForm({...employeeForm, phone: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Email *</label>
                  <input
                    type="email"
                    required
                    value={employeeForm.email}
                    onChange={e => setEmployeeForm({...employeeForm, email: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddEmployee(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Add Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Process Payroll Modal */}
      {showProcessPayroll && selectedEmployee && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ margin: '0 0 8px 0', color: '#1e3a5f' }}>Process Payroll</h2>
            <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>
              {selectedEmployee.first_name} {selectedEmployee.last_name} ({selectedEmployee.employee_id})
            </p>
            
            <div style={{ 
              background: '#f8fafc', 
              padding: '12px', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Pay Type:</span>
                <strong>{selectedEmployee.pay_type === 'hourly' ? 'Hourly' : 'Salary'}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Pay Rate:</span>
                <strong>{formatCurrency(selectedEmployee.pay_rate)}{selectedEmployee.pay_type === 'hourly' ? '/hr' : '/yr'}</strong>
              </div>
            </div>
            
            <form onSubmit={handleProcessPayroll}>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Period Start</label>
                    <input
                      type="date"
                      required
                      value={payrollForm.pay_period_start}
                      onChange={e => setPayrollForm({...payrollForm, pay_period_start: e.target.value})}
                      style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Period End</label>
                    <input
                      type="date"
                      required
                      value={payrollForm.pay_period_end}
                      onChange={e => setPayrollForm({...payrollForm, pay_period_end: e.target.value})}
                      style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Pay Date</label>
                  <input
                    type="date"
                    required
                    value={payrollForm.pay_date}
                    onChange={e => setPayrollForm({...payrollForm, pay_date: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
                {selectedEmployee.pay_type === 'hourly' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Regular Hours</label>
                      <input
                        type="number"
                        step="0.5"
                        value={payrollForm.hours_worked}
                        onChange={e => setPayrollForm({...payrollForm, hours_worked: e.target.value})}
                        style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Overtime Hours</label>
                      <input
                        type="number"
                        step="0.5"
                        value={payrollForm.overtime_hours}
                        onChange={e => setPayrollForm({...payrollForm, overtime_hours: e.target.value})}
                        style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>Other Deductions</label>
                  <input
                    type="number"
                    step="0.01"
                    value={payrollForm.other_deductions}
                    onChange={e => setPayrollForm({...payrollForm, other_deductions: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                </div>
              </div>
              
              <div style={{ 
                background: '#fef3c7', 
                padding: '12px', 
                borderRadius: '8px',
                marginTop: '16px',
                fontSize: '0.875rem'
              }}>
                <strong>Auto-calculated deductions:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Federal Income Tax (based on filing status)</li>
                  <li>Social Security (6.2%)</li>
                  <li>Medicare (1.45%)</li>
                  <li>Tennessee State Tax: $0 (no state income tax)</li>
                </ul>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setShowProcessPayroll(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="process-payroll-submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Process & Generate Check
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Pay Rate Modal */}
      {showEditRate && editingEmployee && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#1e3a5f' }}>Update Pay Rate</h2>
              <button
                onClick={() => setShowEditRate(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <X size={24} color="#64748b" />
              </button>
            </div>
            
            <div style={{ 
              background: '#f8fafc', 
              padding: '12px', 
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#1e3a5f', marginBottom: '4px' }}>
                {editingEmployee.first_name} {editingEmployee.last_name}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                {editingEmployee.employee_id} • {editingEmployee.position}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '8px' }}>
                Current Rate: <strong>{formatCurrency(editingEmployee.pay_rate)}{editingEmployee.pay_type === 'hourly' ? '/hr' : '/yr'}</strong>
              </div>
            </div>
            
            <form onSubmit={handleUpdatePayRate}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '0.875rem' }}>
                  New Pay Rate ({editingEmployee.pay_type === 'hourly' ? '$/hour' : '$/year'})
                </label>
                <input
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  value={newPayRate}
                  onChange={e => setNewPayRate(e.target.value)}
                  data-testid="new-pay-rate-input"
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    border: '2px solid #1e3a5f', 
                    borderRadius: '8px',
                    fontSize: '1.25rem',
                    fontWeight: 'bold'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowEditRate(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="update-pay-rate-submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#1e3a5f',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Update Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Payroll Entry Modal */}
      {showEditPayroll && editingPayroll && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '32px',
            width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, color: '#1e3a5f' }}>Edit Payroll Entry</h2>
              <button onClick={() => setShowEditPayroll(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ background: '#f0f9ff', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.85rem' }}>
              <strong>Check #{editingPayroll.check_number}</strong> - {editingPayroll.employee_name}
              <br />
              <span style={{ color: '#64748b' }}>Taxes will be automatically recalculated with 2025 IRS rates</span>
            </div>

            <form onSubmit={handleUpdatePayroll}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                    Hours Worked
                  </label>
                  <input
                    type="number" step="0.5" min="0" required
                    value={editPayrollForm.hours_worked}
                    onChange={e => setEditPayrollForm({...editPayrollForm, hours_worked: e.target.value})}
                    data-testid="edit-hours-worked"
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                    Overtime Hours
                  </label>
                  <input
                    type="number" step="0.5" min="0"
                    value={editPayrollForm.overtime_hours}
                    onChange={e => setEditPayrollForm({...editPayrollForm, overtime_hours: e.target.value})}
                    data-testid="edit-overtime-hours"
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                  Pay Date
                </label>
                <input
                  type="date" required
                  value={editPayrollForm.pay_date}
                  onChange={e => setEditPayrollForm({...editPayrollForm, pay_date: e.target.value})}
                  data-testid="edit-pay-date"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                    Period Start
                  </label>
                  <input
                    type="date" required
                    value={editPayrollForm.pay_period_start}
                    onChange={e => setEditPayrollForm({...editPayrollForm, pay_period_start: e.target.value})}
                    data-testid="edit-period-start"
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                    Period End
                  </label>
                  <input
                    type="date" required
                    value={editPayrollForm.pay_period_end}
                    onChange={e => setEditPayrollForm({...editPayrollForm, pay_period_end: e.target.value})}
                    data-testid="edit-period-end"
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>
                  Other Deductions ($)
                </label>
                <input
                  type="number" step="0.01" min="0"
                  value={editPayrollForm.other_deductions}
                  onChange={e => setEditPayrollForm({...editPayrollForm, other_deductions: e.target.value})}
                  data-testid="edit-other-deductions"
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowEditPayroll(false)}
                  style={{ flex: 1, padding: '12px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="update-payroll-submit"
                  style={{ flex: 1, padding: '12px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}
                >
                  Update & Recalculate Taxes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
